/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListOrganizationRemoteSessionClientSessionsRequest,
  ListOrganizationRemoteSessionClientSessionsSecurity,
} from "../models/operations/listorganizationremotesessionclientsessions.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOrganizationRemoteSessionClientSessionsInfiniteQuery,
  buildOrganizationRemoteSessionClientSessionsQuery,
  OrganizationRemoteSessionClientSessionsInfiniteQueryData,
  OrganizationRemoteSessionClientSessionsPageParams,
  OrganizationRemoteSessionClientSessionsQueryData,
  prefetchOrganizationRemoteSessionClientSessions,
  prefetchOrganizationRemoteSessionClientSessionsInfinite,
  queryKeyOrganizationRemoteSessionClientSessions,
  queryKeyOrganizationRemoteSessionClientSessionsInfinite,
} from "./organizationRemoteSessionClientSessions.core.js";
export {
  buildOrganizationRemoteSessionClientSessionsInfiniteQuery,
  buildOrganizationRemoteSessionClientSessionsQuery,
  type OrganizationRemoteSessionClientSessionsInfiniteQueryData,
  type OrganizationRemoteSessionClientSessionsPageParams,
  type OrganizationRemoteSessionClientSessionsQueryData,
  prefetchOrganizationRemoteSessionClientSessions,
  prefetchOrganizationRemoteSessionClientSessionsInfinite,
  queryKeyOrganizationRemoteSessionClientSessions,
  queryKeyOrganizationRemoteSessionClientSessionsInfinite,
};

export type OrganizationRemoteSessionClientSessionsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listClientSessions organizationRemoteSessions
 *
 * @remarks
 * List the remote_sessions minted against a remote_session_client in the caller's organization. access_token_encrypted and refresh_token_encrypted are never returned. Requires org:read.
 */
export function useOrganizationRemoteSessionClientSessions(
  request: ListOrganizationRemoteSessionClientSessionsRequest,
  security?: ListOrganizationRemoteSessionClientSessionsSecurity | undefined,
  options?: QueryHookOptions<
    OrganizationRemoteSessionClientSessionsQueryData,
    OrganizationRemoteSessionClientSessionsQueryError
  >,
): UseQueryResult<
  OrganizationRemoteSessionClientSessionsQueryData,
  OrganizationRemoteSessionClientSessionsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOrganizationRemoteSessionClientSessionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listClientSessions organizationRemoteSessions
 *
 * @remarks
 * List the remote_sessions minted against a remote_session_client in the caller's organization. access_token_encrypted and refresh_token_encrypted are never returned. Requires org:read.
 */
export function useOrganizationRemoteSessionClientSessionsSuspense(
  request: ListOrganizationRemoteSessionClientSessionsRequest,
  security?: ListOrganizationRemoteSessionClientSessionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    OrganizationRemoteSessionClientSessionsQueryData,
    OrganizationRemoteSessionClientSessionsQueryError
  >,
): UseSuspenseQueryResult<
  OrganizationRemoteSessionClientSessionsQueryData,
  OrganizationRemoteSessionClientSessionsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOrganizationRemoteSessionClientSessionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listClientSessions organizationRemoteSessions
 *
 * @remarks
 * List the remote_sessions minted against a remote_session_client in the caller's organization. access_token_encrypted and refresh_token_encrypted are never returned. Requires org:read.
 */
export function useOrganizationRemoteSessionClientSessionsInfinite(
  request: ListOrganizationRemoteSessionClientSessionsRequest,
  security?: ListOrganizationRemoteSessionClientSessionsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    OrganizationRemoteSessionClientSessionsInfiniteQueryData,
    OrganizationRemoteSessionClientSessionsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    OrganizationRemoteSessionClientSessionsInfiniteQueryData,
    OrganizationRemoteSessionClientSessionsPageParams
  >,
  OrganizationRemoteSessionClientSessionsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    OrganizationRemoteSessionClientSessionsInfiniteQueryData,
    OrganizationRemoteSessionClientSessionsQueryError,
    InfiniteData<
      OrganizationRemoteSessionClientSessionsInfiniteQueryData,
      OrganizationRemoteSessionClientSessionsPageParams
    >,
    QueryKey,
    OrganizationRemoteSessionClientSessionsPageParams
  >({
    ...buildOrganizationRemoteSessionClientSessionsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listClientSessions organizationRemoteSessions
 *
 * @remarks
 * List the remote_sessions minted against a remote_session_client in the caller's organization. access_token_encrypted and refresh_token_encrypted are never returned. Requires org:read.
 */
export function useOrganizationRemoteSessionClientSessionsInfiniteSuspense(
  request: ListOrganizationRemoteSessionClientSessionsRequest,
  security?: ListOrganizationRemoteSessionClientSessionsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    OrganizationRemoteSessionClientSessionsInfiniteQueryData,
    OrganizationRemoteSessionClientSessionsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    OrganizationRemoteSessionClientSessionsInfiniteQueryData,
    OrganizationRemoteSessionClientSessionsPageParams
  >,
  OrganizationRemoteSessionClientSessionsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    OrganizationRemoteSessionClientSessionsInfiniteQueryData,
    OrganizationRemoteSessionClientSessionsQueryError,
    InfiniteData<
      OrganizationRemoteSessionClientSessionsInfiniteQueryData,
      OrganizationRemoteSessionClientSessionsPageParams
    >,
    QueryKey,
    OrganizationRemoteSessionClientSessionsPageParams
  >({
    ...buildOrganizationRemoteSessionClientSessionsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setOrganizationRemoteSessionClientSessionsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      clientId: string;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    },
  ],
  data: OrganizationRemoteSessionClientSessionsQueryData,
): OrganizationRemoteSessionClientSessionsQueryData | undefined {
  const key = queryKeyOrganizationRemoteSessionClientSessions(...queryKeyBase);

  return client.setQueryData<OrganizationRemoteSessionClientSessionsQueryData>(
    key,
    data,
  );
}

export function invalidateOrganizationRemoteSessionClientSessions(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      clientId: string;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationRemoteSessions",
      "list",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOrganizationRemoteSessionClientSessions(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "organizationRemoteSessions", "list"],
  });
}
